import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Assignment as CreatedIcon,
  MedicalServices as DiagnosticIcon,
  Engineering as FieldIcon,
  CheckCircle as ResolvedIcon,
  DoneAll as ClosedIcon
} from '@mui/icons-material';
import { Typography, Box, Chip } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const statusConfig = {
  ABIERTO: { icon: <CreatedIcon />, color: 'grey', label: 'Ticket Creado' },
  EN_DIAGNOSTICO: { icon: <DiagnosticIcon />, color: 'primary', label: 'En Diagnóstico' },
  ASIGNADO_CAMPO: { icon: <FieldIcon />, color: 'warning', label: 'Asignado a Campo' },
  RESUELTO: { icon: <ResolvedIcon />, color: 'success', label: 'Resuelto' },
  CERRADO: { icon: <ClosedIcon />, color: 'secondary', label: 'Cerrado' }
};

// Función segura para formatear fechas
const formatDate = (date) => {
  if (!date) return null;
  try {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return null;
    return format(parsedDate, 'dd/MM/yyyy HH:mm', { locale: es });
  } catch (e) {
    return null;
  }
};

export default function TicketTimeline({ ticket }) {
  const getTimelineItems = () => {
    const items = [];

    // Creación - siempre existe
    if (ticket.created_at) {
      items.push({
        date: ticket.created_at,
        status: 'ABIERTO',
        label: statusConfig.ABIERTO.label,
        description: `Ticket creado por ${ticket.created_by_name || 'Usuario'}`,
        icon: statusConfig.ABIERTO.icon
      });
    }

    // Diagnóstico (cuando se responde)
    if (ticket.responded_at) {
      items.push({
        date: ticket.responded_at,
        status: 'EN_DIAGNOSTICO',
        label: statusConfig.EN_DIAGNOSTICO.label,
        description: ticket.diagnostic_notes || 'Diagnóstico realizado',
        icon: statusConfig.EN_DIAGNOSTICO.icon
      });
    }

    // Asignación a campo
    if (ticket.assigned_technician_name) {
      items.push({
        date: ticket.arrival_time || ticket.last_updated_at,
        status: 'ASIGNADO_CAMPO',
        label: statusConfig.ASIGNADO_CAMPO.label,
        description: `Asignado a: ${ticket.assigned_technician_name}`,
        icon: statusConfig.ASIGNADO_CAMPO.icon
      });
    }

    // Resolución
    if (ticket.resolved_at) {
      items.push({
        date: ticket.resolved_at,
        status: 'RESUELTO',
        label: statusConfig.RESUELTO.label,
        description: ticket.resolution_notes || 'Ticket resuelto',
        icon: statusConfig.RESUELTO.icon
      });
    }

    // Cierre
    if (ticket.closed_at) {
      items.push({
        date: ticket.closed_at,
        status: 'CERRADO',
        label: statusConfig.CERRADO.label,
        description: 'Ticket cerrado',
        icon: statusConfig.CERRADO.icon
      });
    }

    return items;
  };

  const items = getTimelineItems();

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No hay eventos registrados
      </Typography>
    );
  }

  return (
    <Timeline position="right">
      {items.map((item, index) => {
        const formattedDate = formatDate(item.date);
        return (
          <TimelineItem key={index}>
            <TimelineOppositeContent sx={{ flex: 0.2 }}>
              {formattedDate ? (
                <Typography variant="body2" fontWeight="medium">
                  {formattedDate}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Fecha no disponible
                </Typography>
              )}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot color={statusConfig[item.status]?.color || 'grey'}>
                {item.icon}
              </TimelineDot>
              {index < items.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography variant="subtitle2" fontWeight="bold">
                {item.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.description}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
